import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ChevronDown,
  ChevronRight,
  Coins,
  Download,
  FileCheck2,
  MoreHorizontalIcon,
  TicketCheck,
  Trash,
  UserRoundPen,
  UsersRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createDownloadable, formatDate, formatNumber, getUserMembershipWriteFields, getUserName } from 'helpers';
import { useAPI, useGetPilgrim as getPilgrimData, useGetUser } from 'hooks';
import { PreferencesContext } from 'context';
import { defaultCategories, FullPaymentIcon, PartialPaymentIcon } from 'data';
import Locales from 'locales';
import AssociatedPilgrimsList from 'components/shared/AssociatedPilgrimsList';
import CategoryBadge from 'components/shared/CategoryBadge';
import ConfirmationModal from 'components/shared/ConfirmationModal';
import HelpButton from 'components/shared/HelpButton';
import { LockableButton, LockableDropdownMenuItem } from 'components/shared/Lockable';
import Pagination from 'components/shared/Pagination';
import PilgrimStandaloneShell from 'components/shared/PilgrimStandaloneShell';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { Sheet } from 'components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import { AddTripModal, CategoriesModal, EditTripModal, InvoiceModal, PilgrimDetails } from 'pages/Trip/components';
import Header from './components/Header';
import OnboardingModal from './components/OnboardingModal';
import SelectAgencyModal from './components/SelectAgencyModal';
import SelectPaymentMethod from './components/SelectPaymentMethod';

const money = (value) => `${formatNumber(Number(value) || 0)} F CFA`;

const SectionHeading = ({ id, icon: Icon, title, onExport, selectedCount = 0, onGenerate = undefined }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
    <h2 id={id} className="flex items-center gap-2 text-xl font-semibold tracking-[-0.25px] text-[#20372a]">
      <Icon className="size-5 text-[#528064]" aria-hidden="true" /> {title}
    </h2>
    <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
      {selectedCount > 0 && (
        <>
          <span className="text-sm font-medium text-[#477159]">
            {selectedCount} pèlerin{selectedCount > 1 ? 's' : ''} sélectionné{selectedCount > 1 ? 's' : ''}
          </span>
          <LockableButton variant="hajj" size="sm" onClick={onGenerate}>
            <Coins className="size-4" /> Générer une facture
          </LockableButton>
        </>
      )}
      {onExport && (
        <Button variant="ghost" size="sm" className="text-[#53685b]" onClick={onExport}>
          <Download className="size-4" /> Exporter la liste
        </Button>
      )}
    </div>
  </div>
);

SectionHeading.propTypes = {
  id: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  onExport: PropTypes.func,
  selectedCount: PropTypes.number,
  onGenerate: PropTypes.func,
};

const PaymentAmount = ({ pilgrim }) => {
  const { depositTransferred, isInvoiceFullyPaid, isPartiallyPaid } = pilgrim;
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap font-semibold tabular-nums text-[#24432f]">
      {money(depositTransferred)}
      {isInvoiceFullyPaid && (
        <FullPaymentIcon appearance="compact" portalled tooltipClassName="font-[Inter,sans-serif]" />
      )}
      {isPartiallyPaid && (
        <PartialPaymentIcon appearance="compact" portalled tooltipClassName="font-[Inter,sans-serif]" />
      )}
    </span>
  );
};

PaymentAmount.propTypes = { pilgrim: PropTypes.shape({}).isRequired };

const PilgrimIdentity = ({ trip, pilgrim, onDetails }) => {
  const { firstname, lastname, npi } = pilgrim;
  return (
    <div className="min-w-0">
      <p className="break-words font-semibold text-[#20372a]">{getUserName(firstname, lastname)}</p>
      <p className="mt-0.5 text-xs text-[#7a877f]">{npi ? `NPI ${npi}` : 'NPI non renseigné'}</p>
      <button
        type="button"
        className="mt-1 inline-flex min-h-7 items-center gap-1 rounded-sm text-xs font-medium text-[#2e7650] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hajj-primary"
        onClick={(event) => onDetails(trip, event.currentTarget)}
      >
        Voir le dossier <ChevronRight className="size-3.5" aria-hidden="true" />
      </button>
    </div>
  );
};

PilgrimIdentity.propTypes = {
  trip: PropTypes.shape({}).isRequired,
  pilgrim: PropTypes.shape({}).isRequired,
  onDetails: PropTypes.func.isRequired,
};

const PilgrimActions = ({ trip, pilgrim, onInvoice, onEdit, onDelete }) => {
  const { depositTotal, cartTotal, deposits } = pilgrim;
  const canInvoice = depositTotal < cartTotal;
  return (
    <div className="flex items-center justify-end gap-2">
      {canInvoice && (
        <LockableButton variant="outline" size="sm" onClick={() => onInvoice(trip)}>
          <Coins className="size-4" /> Générer une facture
        </LockableButton>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Plus d’actions">
            <MoreHorizontalIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuGroup>
            <LockableDropdownMenuItem onSelect={() => onEdit(trip)}>
              <UserRoundPen /> Modifier
            </LockableDropdownMenuItem>
            {deposits.length === 0 && (
              <LockableDropdownMenuItem className="text-red-700 focus:text-red-700" onSelect={() => onDelete(trip)}>
                <Trash /> Supprimer
              </LockableDropdownMenuItem>
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

PilgrimActions.propTypes = {
  trip: PropTypes.shape({}).isRequired,
  pilgrim: PropTypes.shape({}).isRequired,
  onInvoice: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const PilgrimStatus = ({ trip, pilgrim, onCertificate }) => {
  const { isHealthy, firstname, lastname } = pilgrim;
  const name = getUserName(firstname, lastname);
  return (
    <div>
      {isHealthy === null ? (
        <Badge variant="pending">En attente</Badge>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hajj-primary focus-visible:ring-offset-2"
              aria-label={`Télécharger le certificat médical de ${name}`}
              onClick={() => onCertificate(trip)}
            >
              <Badge variant={isHealthy ? 'success' : 'destructive'}>{isHealthy ? 'Apte' : 'Inapte'}</Badge>
            </button>
          </TooltipTrigger>
          <TooltipContent portalled className="font-[Inter,sans-serif]">
            Télécharger le certificat médical
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

PilgrimStatus.propTypes = {
  trip: PropTypes.shape({}).isRequired,
  pilgrim: PropTypes.shape({}).isRequired,
  onCertificate: PropTypes.func.isRequired,
};

const InvoiceAssociationSummary = ({ invoice, isExpanded, onToggle, controlsId }) => {
  const trips = invoice.trips || [];
  if (trips.length === 0) {
    return <p className="text-sm italic text-gray-500">{invoice.deposit_count || 0} dépôt(s) associé(s)</p>;
  }
  if (trips.length === 1) {
    const [trip] = trips;
    return (
      <div>
        <p className="font-medium text-[#2d4737]">{getUserName(trip.first_name, trip.last_name)}</p>
        {trip.npi && <p className="mt-0.5 text-xs text-gray-500">NPI {trip.npi}</p>}
      </div>
    );
  }
  return (
    <button
      type="button"
      className="inline-flex min-h-8 items-center gap-1 rounded-md font-medium text-hajj-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hajj-primary"
      aria-expanded={isExpanded}
      aria-controls={controlsId}
      onClick={onToggle}
    >
      {trips.length || invoice.deposit_count || 0} pèlerins
      <ChevronDown className={`size-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
    </button>
  );
};

InvoiceAssociationSummary.propTypes = {
  invoice: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    trips: PropTypes.arrayOf(PropTypes.shape({})),
    deposit_count: PropTypes.number,
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  controlsId: PropTypes.string.isRequired,
};

const InvoiceActions = ({ invoice, onPay, onDelete }) =>
  !invoice.is_paid ? (
    <div className="flex justify-end gap-2">
      <LockableButton variant="outline" size="sm" onClick={() => onPay(invoice)}>
        <TicketCheck className="size-4" /> Payer
      </LockableButton>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Plus d’actions">
            <MoreHorizontalIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <LockableDropdownMenuItem className="text-red-700 focus:text-red-700" onSelect={() => onDelete(invoice)}>
            <Trash /> Supprimer
          </LockableDropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  ) : null;

InvoiceActions.propTypes = {
  invoice: PropTypes.shape({ is_paid: PropTypes.bool }).isRequired,
  onPay: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const Profile = () => {
  const { api } = useAPI();
  const queryClient = useQueryClient();
  const { user, isOnboarded, refreshCurrentUser } = useGetUser();
  const { registrationDeposit, pilgrimEligibilityMinAge } = useContext(PreferencesContext);
  const [tripCursor, setTripCursor] = useState('');
  const [invoiceCursor, setInvoiceCursor] = useState('');
  const [selectedPilgrimIds, setSelectedPilgrimIds] = useState([]);
  const [invoicePilgrims, setInvoicePilgrims] = useState([]);
  const [detailsPilgrim, setDetailsPilgrim] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const detailsTriggerRef = useRef(null);
  const pilgrimsSectionRef = useRef(null);
  const [selectedPilgrim, setSelectedPilgrim] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [expandedInvoiceId, setExpandedInvoiceId] = useState(null);
  const [modals, setModals] = useState({ onboarding: !isOnboarded });
  const openModal = (name) => setModals((current) => ({ ...current, [name]: true }));
  const closeModal = (name) => setModals((current) => ({ ...current, [name]: false }));

  const { data: tripAgency } = useQuery({
    queryFn: api.retrieve('pilgrim/group', 'agency/'),
    queryKey: ['pilgrim/group/agency/'],
  });
  const {
    data: trips,
    isFetching: isFetchingTrips,
    isError: isTripsError,
  } = useQuery({
    queryFn: api.list('trip/', { cursor: tripCursor }),
    queryKey: ['trip/', tripCursor],
    keepPreviousData: true,
    retry: false,
  });
  const {
    data: invoices,
    isFetching: isFetchingInvoices,
    isError: isInvoicesError,
  } = useQuery({
    queryFn: api.list('invoice/', { cursor: invoiceCursor }),
    queryKey: ['invoice/', invoiceCursor],
    keepPreviousData: true,
    retry: false,
  });
  const { data: categoriesData } = useQuery({ queryFn: api.list('category/'), queryKey: ['category/'] });
  const { data: agenciesList, refetch: refetchAgencies } = useQuery({
    queryFn: api.list('agency/'),
    queryKey: ['agency/'],
  });

  const allAvailableCategories = Array.isArray(categoriesData)
    ? [...categoriesData].filter((category) => defaultCategories.includes(category.name)).reverse()
    : categoriesData;
  const agencies = agenciesList
    ? agenciesList.filter(
        (agency) =>
          // agency.admin?.is_verified &&
          // agency.admin?.is_active &&
          Array.isArray(agency.category_ids) && selectedCategories.every((id) => agency.category_ids.includes(id))
      )
    : [];
  const agencyAvailableCategories =
    tripAgency && allAvailableCategories
      ? allAvailableCategories.filter((category) => tripAgency.category_ids.includes(category.id))
      : [];
  const selectedPilgrimsData = (trips || []).filter((trip) => selectedPilgrimIds.includes(trip.id));
  const tripRecords = (trips || []).map((trip) => ({ trip, pilgrim: getPilgrimData(trip) }));
  const allSelected = Boolean(trips?.length) && selectedPilgrimIds.length === trips.length;

  useEffect(() => {
    if (!trips || isFetchingTrips) return;
    const visibleIds = new Set(trips.map((trip) => trip.id));
    setSelectedPilgrimIds((current) => current.filter((id) => visibleIds.has(id)));
  }, [isFetchingTrips, trips]);

  const { mutate: deletePilgrim } = useMutation({
    mutationFn: (id) => api.remove('trip', id),
    onSuccess: () => {
      ['trip/', 'trip/stats/default/', 'trip/stats/category/', 'category/', 'trip/groupes/', 'agency/'].forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      );
      queryClient.invalidateQueries({ queryKey: ['agency/', 'is_sa'] });
      toast.success(Locales.trip.toast.delete);
    },
  });
  const { mutate: deleteInvoice } = useMutation({
    mutationFn: (invoice) => api.remove('invoice', invoice.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/groupes/'] });
      toast.success(Locales.invoice.toast.delete_invoice);
    },
  });
  const { mutate: selectAgency } = useMutation({
    mutationFn: () => api.post(`pilgrim/select_agency_create_group/${selectedAgency}/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['pilgrim/group/agency/'] });
    },
  });
  const { mutate: generateMedicalCertificate } = useMutation({
    mutationFn: ({ id }) => api.get(`trip/${id}/generate_medical_certificate/`, { responseType: 'blob' }),
    onSuccess: ({ data }, { trip }) => {
      createDownloadable(
        data,
        `certificat_medicale_de_${getUserName(trip.pilgrim?.first_name, trip.pilgrim?.last_name)}_${
          trip.pilgrim?.passport || trip.pilgrim?.npi
        }.pdf`,
        'application/pdf'
      );
      toast.success('Téléchargement réussi');
    },
  });
  const { mutate: exportPilgrims } = useMutation({
    mutationFn: () => api.get('trip/export/csv/'),
    onSuccess: ({ data }) => {
      createDownloadable(data, 'Liste des pèlerins', 'csv');
      toast.success('Téléchargement réussi');
    },
  });
  const { mutate: exportInvoices } = useMutation({
    mutationFn: () => api.get('invoice/export/csv/'),
    onSuccess: ({ data }) => {
      createDownloadable(data, 'Liste des factures', 'csv');
      toast.success('Téléchargement réussi');
    },
  });
  const { mutate: updateOnboardingStatus } = useMutation({
    mutationFn: () =>
      api.update(
        'user',
        {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          ...getUserMembershipWriteFields(user),
          is_onboard: true,
        },
        user.id
      ),
    onSuccess: async () => {
      await refreshCurrentUser();
      queryClient.invalidateQueries({ queryKey: ['user/'] });
    },
  });

  const showDetails = (trip, trigger) => {
    detailsTriggerRef.current = trigger;
    setDetailsPilgrim(trip);
    setIsDetailsOpen(true);
  };
  const closeDetails = () => {
    setIsDetailsOpen(false);
  };
  const openInvoice = (pilgrimsToInvoice) => {
    setInvoicePilgrims(pilgrimsToInvoice);
    openModal('invoice');
  };
  const openEdit = (trip) => {
    setSelectedPilgrim(trip);
    openModal('editPilgrim');
  };
  const openDeletePilgrim = (trip) => {
    setSelectedPilgrim(trip);
    openModal('deletePilgrim');
  };
  const setTripPage = (cursor) => {
    setSelectedPilgrimIds([]);
    setTripCursor(cursor);
  };
  const validationMethod = (invoice) => {
    if (invoice.bank_name) return invoice.bank_name;
    if (invoice.payment_method === 'MOBILE_MONEY') return 'Mobile Money';
    if (invoice.payment_method === 'CARD') return 'Carte Bancaire';
    return '-';
  };
  const openInvoicePayment = (invoice) => {
    setSelectedInvoice(invoice);
    openModal('payment');
  };
  const openDeleteInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    openModal('deleteInvoice');
  };

  return (
    <PilgrimStandaloneShell>
      <main className="mx-auto min-h-screen w-full max-w-[1520px] px-4 py-7 sm:px-7 lg:px-10 lg:py-10">
        {modals.onboarding && (
          <OnboardingModal
            isOpened
            isOnboarded={isOnboarded}
            updateOnboardingStatus={updateOnboardingStatus}
            closeModal={() => closeModal('onboarding')}
          />
        )}
        <HelpButton onClick={() => openModal('onboarding')} />
        <div className="space-y-8">
          <Header
            agency={tripAgency}
            categories={allAvailableCategories}
            onOpenAgencyModal={() => openModal('agency')}
            onOpenAddPilgrimModal={() => openModal('addPilgrim')}
          />

          <section
            ref={pilgrimsSectionRef}
            className="space-y-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hajj-primary"
            aria-labelledby="pilgrims-heading"
            tabIndex={-1}
          >
            <div>
              <SectionHeading
                id="pilgrims-heading"
                icon={UsersRound}
                title="Mes pèlerins"
                onExport={trips?.length ? exportPilgrims : undefined}
                selectedCount={selectedPilgrimIds.length}
                onGenerate={() => openInvoice(selectedPilgrimsData)}
              />
            </div>
            {isFetchingTrips && trips && (
              <p className="sr-only" role="status">
                Actualisation des pèlerins…
              </p>
            )}
            {isTripsError ? (
              <div className="rounded-[10px] border bg-white p-6 text-sm text-red-700">
                Impossible de charger vos pèlerins.
              </div>
            ) : !trips && isFetchingTrips ? (
              <div className="rounded-[10px] border bg-white p-6 text-sm text-gray-500">Chargement des pèlerins…</div>
            ) : trips?.length ? (
              <div className="overflow-hidden rounded-[10px] border border-[#dce4df] bg-white">
                <div className="hidden md:block">
                  <Table
                    className="min-w-[1120px] text-sm"
                    containerProps={{
                      tabIndex: 0,
                      role: 'region',
                      'aria-label': 'Tableau des pèlerins, défilement horizontal',
                    }}
                  >
                    <TableHeader className="bg-[#f2f6f3]">
                      <TableRow>
                        <TableHead className="w-12 px-5">
                          <Checkbox
                            aria-label="Sélectionner tous les pèlerins de cette page"
                            checked={allSelected ? true : selectedPilgrimIds.length ? 'indeterminate' : false}
                            onCheckedChange={() =>
                              setSelectedPilgrimIds(allSelected ? [] : trips.map((trip) => trip.id))
                            }
                            disabled={isFetchingTrips}
                          />
                        </TableHead>
                        <TableHead>Pèlerin</TableHead>
                        <TableHead>Catégorie</TableHead>
                        <TableHead>Santé</TableHead>
                        <TableHead>Passeport</TableHead>
                        <TableHead>Montant payé</TableHead>
                        <TableHead>Montant à payer</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tripRecords.map(({ trip, pilgrim }) => {
                        const isSelected = selectedPilgrimIds.includes(trip.id);
                        return (
                          <TableRow
                            key={trip.id}
                            className={isSelected ? 'bg-[#f0f7f2] hover:bg-[#edf5ef]' : 'bg-white'}
                          >
                            <TableCell className="px-5">
                              <Checkbox
                                aria-label={`Sélectionner ${getUserName(pilgrim.firstname, pilgrim.lastname)}`}
                                checked={isSelected}
                                disabled={isFetchingTrips}
                                onCheckedChange={() =>
                                  setSelectedPilgrimIds((current) =>
                                    current.includes(trip.id)
                                      ? current.filter((id) => id !== trip.id)
                                      : [...current, trip.id]
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <PilgrimIdentity trip={trip} pilgrim={pilgrim} onDetails={showDetails} />
                            </TableCell>
                            <TableCell>
                              <CategoryBadge
                                categoryName={pilgrim.categoryName}
                                recoverable
                                portalled
                                tooltipClassName="font-[Inter,sans-serif]"
                              />
                            </TableCell>
                            <TableCell>
                              <PilgrimStatus
                                trip={trip}
                                pilgrim={pilgrim}
                                onCertificate={(item) => generateMedicalCertificate({ id: item.id, trip: item })}
                              />
                            </TableCell>
                            <TableCell>
                              {pilgrim.isApprovedForMAECStep ? (
                                <Badge variant="success">
                                  <FileCheck2 className="size-3.5" /> Déposé
                                </Badge>
                              ) : (
                                <Badge variant="pending">En attente</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <PaymentAmount pilgrim={pilgrim} />
                            </TableCell>
                            <TableCell className="whitespace-nowrap tabular-nums">{money(pilgrim.cartTotal)}</TableCell>
                            <TableCell>
                              <PilgrimActions
                                trip={trip}
                                pilgrim={pilgrim}
                                onInvoice={(item) => openInvoice([item])}
                                onEdit={openEdit}
                                onDelete={openDeletePilgrim}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <div className="divide-y md:hidden">
                  {tripRecords.map(({ trip, pilgrim }) => {
                    const selected = selectedPilgrimIds.includes(trip.id);
                    return (
                      <article key={trip.id} className={`p-4 ${selected ? 'bg-[#f0f7f2]' : 'bg-white'}`}>
                        <div className="flex items-start gap-3">
                          <Checkbox
                            className="mt-1"
                            checked={selected}
                            disabled={isFetchingTrips}
                            aria-label={`Sélectionner ${getUserName(pilgrim.firstname, pilgrim.lastname)}`}
                            onCheckedChange={() =>
                              setSelectedPilgrimIds((current) =>
                                current.includes(trip.id)
                                  ? current.filter((id) => id !== trip.id)
                                  : [...current, trip.id]
                              )
                            }
                          />
                          <div className="min-w-0 flex-1">
                            <PilgrimIdentity trip={trip} pilgrim={pilgrim} onDetails={showDetails} />
                          </div>
                        </div>
                        <dl className="mt-4 grid grid-cols-2 gap-3 text-xs">
                          <div>
                            <dt className="text-gray-500">Catégorie</dt>
                            <dd className="mt-1">
                              <CategoryBadge
                                categoryName={pilgrim.categoryName}
                                recoverable
                                portalled
                                tooltipClassName="font-[Inter,sans-serif]"
                              />
                            </dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">Montant payé</dt>
                            <dd className="mt-1">
                              <PaymentAmount pilgrim={pilgrim} />
                            </dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">Santé</dt>
                            <dd className="mt-1">
                              <PilgrimStatus
                                trip={trip}
                                pilgrim={pilgrim}
                                onCertificate={(item) => generateMedicalCertificate({ id: item.id, trip: item })}
                              />
                            </dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">Passeport</dt>
                            <dd className="mt-1">
                              {pilgrim.isApprovedForMAECStep ? (
                                <Badge variant="success">Déposé</Badge>
                              ) : (
                                <Badge variant="pending">En attente</Badge>
                              )}
                            </dd>
                          </div>
                          <div className="col-span-2">
                            <dt className="text-gray-500">Montant à payer</dt>
                            <dd className="mt-1 font-medium tabular-nums">{money(pilgrim.cartTotal)}</dd>
                          </div>
                        </dl>
                        <div className="mt-4 border-t pt-3">
                          <PilgrimActions
                            trip={trip}
                            pilgrim={pilgrim}
                            onInvoice={(item) => openInvoice([item])}
                            onEdit={openEdit}
                            onDelete={openDeletePilgrim}
                          />
                        </div>
                      </article>
                    );
                  })}
                </div>
                <Pagination data={trips} isFetching={isFetchingTrips} setCursor={setTripPage} />
              </div>
            ) : (
              <div className="rounded-[10px] border bg-white p-6">
                <p className="font-semibold text-[#294234]">
                  {tripAgency ? 'Aucun pèlerin pour le moment' : 'Votre parcours commence ici'}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {tripAgency
                    ? 'Ajoutez votre premier pèlerin pour commencer son dossier.'
                    : 'Sélectionnez d’abord une société agréée pour ajouter vos pèlerins.'}
                </p>
              </div>
            )}
          </section>

          <section className="space-y-3" aria-labelledby="invoices-heading">
            <div>
              <SectionHeading
                id="invoices-heading"
                icon={FileCheck2}
                title="Mes factures"
                onExport={invoices?.length ? exportInvoices : undefined}
                selectedCount={0}
              />
            </div>
            {isFetchingInvoices && invoices && (
              <p className="sr-only" role="status">
                Actualisation des factures…
              </p>
            )}
            {isInvoicesError ? (
              <div className="rounded-[10px] border bg-white p-6 text-sm text-red-700">
                Impossible de charger vos factures.
              </div>
            ) : !invoices && isFetchingInvoices ? (
              <div className="rounded-[10px] border bg-white p-6 text-sm text-gray-500">Chargement des factures…</div>
            ) : invoices?.length ? (
              <div className="overflow-hidden rounded-[10px] border border-[#dce4df] bg-white">
                <div className="divide-y md:hidden">
                  {invoices.map((invoice) => {
                    const expanded = expandedInvoiceId === invoice.id;
                    return (
                      <article key={invoice.id} className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-xs text-gray-500">Référence</p>
                            <p className="mt-0.5 font-semibold text-[#294234]">{invoice.code}</p>
                          </div>
                          <Badge variant={invoice.is_paid ? 'success' : 'pending'}>
                            {invoice.is_paid ? 'Validé' : 'En attente'}
                          </Badge>
                        </div>
                        <div className="mt-3">
                          <InvoiceAssociationSummary
                            invoice={invoice}
                            isExpanded={expanded}
                            controlsId={`profile-mobile-invoice-associations-${invoice.id}`}
                            onToggle={() => setExpandedInvoiceId(expanded ? null : invoice.id)}
                          />
                        </div>
                        {expanded && (invoice.trips?.length || 0) > 1 && (
                          <div
                            id={`profile-mobile-invoice-associations-${invoice.id}`}
                            className="mt-3 bg-[#fafbfa] p-3"
                          >
                            <h3 className="mb-2 text-sm font-semibold">Pèlerins associés ({invoice.trips.length})</h3>
                            <AssociatedPilgrimsList
                              trips={invoice.trips}
                              fallbackCount={invoice.deposit_count}
                              invoiceCode={invoice.code}
                            />
                          </div>
                        )}
                        <dl className="mt-4 grid grid-cols-2 gap-3 text-xs">
                          <div>
                            <dt className="text-gray-500">Montant</dt>
                            <dd className="mt-1 font-semibold tabular-nums">{money(invoice.amount)}</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">Créée le</dt>
                            <dd className="mt-1">{formatDate(invoice.created)}</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">Payée le</dt>
                            <dd className="mt-1">{invoice.paid ? formatDate(invoice.paid) : '-'}</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">Validé par</dt>
                            <dd className="mt-1">{invoice.is_paid ? validationMethod(invoice) : '-'}</dd>
                          </div>
                        </dl>
                        {!invoice.is_paid && (
                          <div className="mt-4 border-t pt-3">
                            <InvoiceActions invoice={invoice} onPay={openInvoicePayment} onDelete={openDeleteInvoice} />
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
                <div className="hidden md:block">
                  <Table
                    className="min-w-[1120px] text-sm"
                    containerProps={{
                      tabIndex: 0,
                      role: 'region',
                      'aria-label': 'Tableau des factures, défilement horizontal',
                    }}
                  >
                    <TableHeader className="bg-[#f2f6f3]">
                      <TableRow>
                        <TableHead>Référence</TableHead>
                        <TableHead>Pèlerin</TableHead>
                        <TableHead>Montant</TableHead>
                        <TableHead>Date de création</TableHead>
                        <TableHead>Date de paiement</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Validé par</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((invoice) => {
                        const expanded = expandedInvoiceId === invoice.id;
                        return (
                          <React.Fragment key={invoice.id}>
                            <TableRow className="bg-white">
                              <TableCell className="font-semibold">{invoice.code}</TableCell>
                              <TableCell>
                                <InvoiceAssociationSummary
                                  invoice={invoice}
                                  isExpanded={expanded}
                                  controlsId={`profile-desktop-invoice-associations-${invoice.id}`}
                                  onToggle={() => setExpandedInvoiceId(expanded ? null : invoice.id)}
                                />
                              </TableCell>
                              <TableCell className="whitespace-nowrap font-semibold tabular-nums">
                                {money(invoice.amount)}
                              </TableCell>
                              <TableCell>{formatDate(invoice.created)}</TableCell>
                              <TableCell>{invoice.paid ? formatDate(invoice.paid) : '-'}</TableCell>
                              <TableCell>
                                <Badge variant={invoice.is_paid ? 'success' : 'pending'}>
                                  {invoice.is_paid ? 'Validé' : 'En attente'}
                                </Badge>
                              </TableCell>
                              <TableCell>{invoice.is_paid ? validationMethod(invoice) : '-'}</TableCell>
                              <TableCell>
                                <InvoiceActions
                                  invoice={invoice}
                                  onPay={openInvoicePayment}
                                  onDelete={openDeleteInvoice}
                                />
                              </TableCell>
                            </TableRow>
                            {expanded && (invoice.trips?.length || 0) > 1 && (
                              <TableRow
                                id={`profile-desktop-invoice-associations-${invoice.id}`}
                                className="bg-[#fafbfa] hover:bg-[#fafbfa]"
                              >
                                <TableCell colSpan={8} className="p-0">
                                  <div className="px-5 py-3">
                                    <h3 className="mb-2 text-sm font-semibold">
                                      Pèlerins associés ({invoice.trips.length})
                                    </h3>
                                    <AssociatedPilgrimsList
                                      trips={invoice.trips}
                                      fallbackCount={invoice.deposit_count}
                                      invoiceCode={invoice.code}
                                    />
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <Pagination data={invoices} isFetching={isFetchingInvoices} setCursor={setInvoiceCursor} />
              </div>
            ) : (
              <div className="rounded-[10px] border bg-white p-6">
                <p className="font-semibold text-[#294234]">Aucune facture pour le moment</p>
                <p className="mt-1 text-sm text-gray-500">Les factures générées pour vos pèlerins apparaîtront ici.</p>
              </div>
            )}
          </section>
        </div>

        <Sheet open={isDetailsOpen} onOpenChange={(open) => !open && closeDetails()}>
          {detailsPilgrim && (
            <PilgrimDetails
              pilgrimData={detailsPilgrim}
              handleGenerateMedicalCertificate={(trip) => generateMedicalCertificate({ id: trip.id, trip })}
              onOpenAutoFocus={(event) => {
                event.preventDefault();
                event.currentTarget.querySelector('button')?.focus();
              }}
              onCloseAutoFocus={(event) => {
                event.preventDefault();
                window.setTimeout(() => {
                  const trigger = detailsTriggerRef.current;
                  (trigger?.isConnected ? trigger : pilgrimsSectionRef.current)?.focus();
                }, 0);
                setDetailsPilgrim(null);
              }}
            />
          )}
        </Sheet>
        {modals.payment && selectedInvoice && (
          <SelectPaymentMethod
            registrationDeposit={registrationDeposit}
            isOpened
            invoice={selectedInvoice}
            agency={tripAgency}
            handleOpenKkiapay={() => closeModal('payment')}
            closeModal={() => closeModal('payment')}
          />
        )}
        {modals.addPilgrim && (
          <AddTripModal
            isOpened
            isPilgrimUser
            pilgrimEligibilityMinAge={pilgrimEligibilityMinAge}
            categories={agencyAvailableCategories}
            openCategoriesModal={() => openModal('categories')}
            closeModal={() => closeModal('addPilgrim')}
          />
        )}
        <CategoriesModal
          isOpened={Boolean(modals.categories)}
          isPilgrimUser
          categories={allAvailableCategories}
          closeModal={() => closeModal('categories')}
        />
        {modals.editPilgrim && selectedPilgrim && (
          <EditTripModal
            isOpened
            pilgrimData={selectedPilgrim}
            isPilgrimUser
            pilgrimEligibilityMinAge={pilgrimEligibilityMinAge}
            categories={allAvailableCategories}
            registrationDeposit={registrationDeposit}
            openCategoriesModal={() => openModal('categories')}
            closeModal={() => closeModal('editPilgrim')}
          />
        )}
        {modals.invoice && (
          <InvoiceModal
            isOpened
            selectedPilgrimsData={invoicePilgrims}
            advanceDeposit={registrationDeposit}
            closeModal={() => closeModal('invoice')}
          />
        )}
        {modals.agency && (
          <SelectAgencyModal
            isOpened
            categories={allAvailableCategories}
            agencies={agencies}
            selectedAgency={selectedAgency}
            refetchAgencies={refetchAgencies}
            setSelectedAgency={setSelectedAgency}
            onConfirmAgency={() => {
              closeModal('agency');
              setSelectedCategories([]);
              openModal('agencyConfirmation');
            }}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            closeModal={() => {
              setSelectedAgency(null);
              setSelectedCategories([]);
              closeModal('agency');
            }}
          />
        )}
        {selectedPilgrim && (
          <ConfirmationModal
            isOpen={Boolean(modals.deletePilgrim)}
            setOpenModal={(open) => !open && closeModal('deletePilgrim')}
            title={Locales.trip.modal.title.delete_trip}
            confirmBtnVariant="destructive"
            confirmBtnLabel={Locales.base.button.delete}
            message={
              <p>
                {Locales.formatString(Locales.trip.modal.text.delete_trip, {
                  fullname: (
                    <strong>
                      {getUserName(selectedPilgrim.pilgrim.first_name, selectedPilgrim.pilgrim.last_name)}
                    </strong>
                  ),
                })}
              </p>
            }
            onConfirm={() => deletePilgrim(selectedPilgrim.id)}
          />
        )}
        {selectedInvoice && (
          <ConfirmationModal
            isOpen={Boolean(modals.deleteInvoice)}
            setOpenModal={(open) => !open && closeModal('deleteInvoice')}
            title={Locales.invoice.action_modal.title.delete}
            confirmBtnVariant="destructive"
            confirmBtnLabel={Locales.base.button.delete}
            message={
              <p>
                {Locales.formatString(Locales.invoice.delete_payment, {
                  amount: <strong>{formatNumber(selectedInvoice.amount)}</strong>,
                  code: <strong>{selectedInvoice.code}</strong>,
                })}
              </p>
            }
            onConfirm={() => deleteInvoice(selectedInvoice)}
          />
        )}
        {selectedAgency && (
          <ConfirmationModal
            isOpen={Boolean(modals.agencyConfirmation)}
            setOpenModal={(open) => !open && closeModal('agencyConfirmation')}
            title="Choisir une société agréée"
            confirmBtnLabel={Locales.base.button.save}
            message={
              <p>
                {Locales.formatString(Locales.trip.modal.text.choose_agency, {
                  agency: <strong>{agencies.find((agency) => agency.id === selectedAgency)?.name}</strong>,
                })}
              </p>
            }
            onConfirm={selectAgency}
          />
        )}
      </main>
    </PilgrimStandaloneShell>
  );
};

export default Profile;
